import React, { useState, useEffect } from "react";
import { Group, Divider, Title, TextInput, NumberInput } from "@mantine/core";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";

import { Counter } from "@components/shared";
import Intervals from "@components/Contest/ContestSettings/Intervals";

import useAppStore from "./effort-settings-store";

const isNum = (num) => new RegExp("^[0-9]+$").test(num);

export default function RideChallengeSettings({
  onUpdate,
  isVehicleDataEffort,
  beginDate = null,
  endDate = null,
}) {
  const { t } = useTranslation();
  const { settings } = useAppStore(
    (state) => ({
      settings: state.settings,
    }),
    shallow
  );

  const [tempValues, setTempValues] = useState({
    max_daily_mileage: "",
    vin_user_limit: "",
    daily_mileage_limit: "",
    readings_limit: "",
  });

  useEffect(() => {
    if (!settings.rev_score) return;

    setTempValues({
      ...tempValues,
      daily_mileage_limit:
        settings?.ride_challenge_settings?.daily_mileage_limit || "",
      max_daily_mileage:
        settings?.ride_challenge_warnings?.max_daily_mileage || "",
      vin_user_limit: settings?.ride_challenge_warnings?.vin_user_limit || "",
      readings_limit: settings?.ride_challenge_settings?.readings_limit || "",
    });
  }, [JSON.stringify(settings)]);

  if (!settings.rev_score) return null;

  return (
    <div>
      {/* <Title order={4}>Keywords</Title>
      <TextInput
        label="Keyword Claim Message"
        value={rideChallengeText.keyword_claim_message}
        onChange={(e) =>
          setTempTextValues({
            ...tempTextValues,
            keyword_claim_message: e.target.value,
          })
        }
        onBlur={() => {
          onUpdate({
            ride_challenge_text: {
              ...rideChallengeText,
              keyword_claim_message: tempTextValues.keyword_claim_message,
            },
          });
        }}
      />
      <Divider my="lg" /> */}
      {!isVehicleDataEffort && (
        <Title order={4}>
          {t("mixer:effort:ride_challenge_settings_title")}
        </Title>
      )}
      <NumberInput
        label={t("mixer:effort:daily_mileage_limit_title")}
        allowDecimal={false}
        allowNegative={false}
        allowLeadingZeros={false}
        value={tempValues.daily_mileage_limit}
        onChange={(e) =>
          setTempValues({
            ...tempValues,
            daily_mileage_limit: e,
          })
        }
        onBlur={() => {
          if (!isNum(tempValues.daily_mileage_limit)) {
            return setTempValues({
              ...tempValues,
              daily_mileage_limit: "",
            });
          }
          onUpdate({
            ride_challenge_settings: {
              ...settings.ride_challenge_settings,
              daily_mileage_limit: parseInt(tempValues.daily_mileage_limit),
            },
          });
        }}
      />
      <NumberInput
        label={t("mixer:effort:readings_limit_title")}
        allowDecimal={false}
        allowNegative={false}
        allowLeadingZeros={false}
        value={tempValues.readings_limit}
        onChange={(e) =>
          setTempValues({
            ...tempValues,
            readings_limit: e,
          })
        }
        onBlur={() => {
          if (!isNum(tempValues.readings_limit)) {
            return setTempValues({
              ...tempValues,
              readings_limit: "",
            });
          }
          onUpdate({
            ride_challenge_settings: {
              ...settings.ride_challenge_settings,
              readings_limit: parseInt(tempValues.readings_limit),
            },
          });
        }}
      />
      {!isVehicleDataEffort && (
        <>
          <Divider my="lg" />
          <Title order={4}>{t("common:warning_plural")}</Title>
          <NumberInput
            label={t("mixer:effort:max_daily_mileage_title")}
            allowDecimal={false}
            allowNegative={false}
            allowLeadingZeros={false}
            value={tempValues.max_daily_mileage}
            onChange={(e) =>
              setTempValues({
                ...tempValues,
                max_daily_mileage: e,
              })
            }
            onBlur={() => {
              if (!isNum(tempValues.max_daily_mileage)) {
                return setTempValues({
                  ...tempValues,
                  max_daily_mileage: "",
                });
              }
              onUpdate({
                ride_challenge_warnings: {
                  ...settings.ride_challenge_warnings,
                  max_daily_mileage: parseInt(tempValues.max_daily_mileage),
                },
              });
            }}
          />
          <NumberInput
            label={t("mixer:effort:vin_user_limit_title")}
            allowDecimal={false}
            allowNegative={false}
            allowLeadingZeros={false}
            value={tempValues.vin_user_limit}
            onChange={(e) =>
              setTempValues({
                ...tempValues,
                vin_user_limit: e,
              })
            }
            onBlur={() => {
              if (!isNum(tempValues.vin_user_limit)) {
                return setTempValues({
                  ...tempValues,
                  vin_user_limit: "",
                });
              }
              onUpdate({
                ride_challenge_warnings: {
                  ...settings.ride_challenge_warnings,
                  vin_user_limit: parseInt(tempValues.vin_user_limit),
                },
              });
            }}
          />
          <Divider my="lg" />
          <Title order={4} mb="sm">
            {t("mixer:effort:engagement_multipliers_title")}
          </Title>
          <Group mb="lg">
            <Counter
              unlimited={false}
              minValue={1}
              value={settings.rev_score.keywords_engagement_multiplier}
              onChange={(e) => {
                onUpdate({
                  rev_score: {
                    ...settings.rev_score,
                    keywords_engagement_multiplier: e,
                  },
                });
              }}
            />
            <Title order={3}>
              {t("mixer:effort:keywords_engagement_multiplier_title")}
            </Title>
          </Group>
          <Group mb="lg">
            <Counter
              unlimited={false}
              minValue={1}
              value={settings.rev_score.readings_engagement_multiplier}
              onChange={(e) => {
                onUpdate({
                  rev_score: {
                    ...settings.rev_score,
                    readings_engagement_multiplier: e,
                  },
                });
              }}
            />
            <Title order={3}>
              {t("mixer:effort:readings_engagement_multiplier_title")}
            </Title>
          </Group>
          <Group>
            <Counter
              unlimited={false}
              minValue={1}
              value={settings.rev_score.locations_engagement_multiplier || 0}
              onChange={(e) => {
                onUpdate({
                  rev_score: {
                    ...settings.rev_score,
                    locations_engagement_multiplier: e,
                  },
                });
              }}
            />
            <Title order={3}>
              {t("mixer:effort:locations_engagement_multiplier_title")}
            </Title>
          </Group>
        </>
      )}
    </div>
  );
}
